import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, serverTimestamp, orderBy, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import Orders from './Orders';
import Messages from './Messages';
import '../../styles/Admin.css';
import { 
  addProduct, 
  updateProduct, 
  deleteProduct, 
  getProducts, 
  addCategory, 
  getCategories, 
  updateCategory, 
  deleteCategory,
  addMarketer,
  deleteUser
} from '../../firebase/services';
import ImageUpload from '../../components/common/ImageUpload';
import EnhancedProductFormWithTranslation from '../../components/forms/EnhancedProductFormWithTranslation/EnhancedProductFormWithTranslation';
import EnhancedBlogFormWithTranslation from '../../components/forms/EnhancedBlogFormWithTranslation/EnhancedBlogFormWithTranslation';

const Admin = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [isSorting, setIsSorting] = useState(false);

  const [productData, setProductData] = useState({
    name: '',
    price: '',
    description: '',
    category: '',
    mainImage: '',
    additionalImages: [],
    sizes: [],
    colors: [],
    isOffer: false,
    offerPrice: '',
    isAman: false,
    amanPrice: '',
    wholesaleDiscount: ''
  });

  const [categoryData, setCategoryData] = useState({
    name: '',
    description: ''
  });

  const [blogs, setBlogs] = useState([]);
  const [newBlog, setNewBlog] = useState({
    title: '',
    summary: '',
    content: '',
    imageUrl: '',
    author: ''
  });
  const [editingBlog, setEditingBlog] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); 
  const [marketers, setMarketers] = useState([]);
  const [newMarketer, setNewMarketer] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [marketerCredentials, setMarketerCredentials] = useState({ email: '', password: '' });
  const [showDeleteForm, setShowDeleteForm] = useState(null);

  const [winnerData, setWinnerData] = useState({
    name: '',
    prize: '',
    imageUrl: '',
    date: '',
    announcementDateTime: '', // Add this new field
    time: '' // Add this for form handling
  });
  const [winners, setWinners] = useState([]);
  const [editingWinner, setEditingWinner] = useState(null);

  useEffect(() => {
    loadCategories();
    loadProducts();
    fetchBlogs();
    fetchMarketers();
    fetchWinners();
  }, []);

  const loadCategories = async () => {
    try {
      const result = await getCategories();
      if (result.success) {
        setCategories(result.categories);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    }
  };

  const loadProducts = async () => {
    try {
      const result = await getProducts();
      if (result.success) {
        setProducts(result.products);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogs = async () => {
    try {
      const blogsRef = collection(db, 'blogs');
      const snapshot = await getDocs(blogsRef);
      const blogsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBlogs(blogsData);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to fetch blogs');
    }
  };

  const fetchMarketers = async () => {
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'marketer'));
      const querySnapshot = await getDocs(q);
      const marketersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMarketers(marketersData);
    } catch (err) {
      console.error('Error fetching marketers:', err);
      setError('Failed to fetch marketers');
    } finally {
      setLoading(false);
    }
  };

  const fetchWinners = async () => {
    try {
      const winnersSnapshot = await getDocs(collection(db, 'winners'));
      const winnersData = winnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWinners(winnersData);
    } catch (err) {
      console.error('Error fetching winners:', err);
    }
  };

  const handleEnhancedProductSubmit = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      let result;
      const productDataToSave = {
        name: formData.name,
        price: Number(formData.price),
        description: formData.description,
        category: formData.category,
        mainImage: formData.mainImage,
        additionalImages: formData.additionalImages || [],
        sizes: formData.sizes || [],
        colors: formData.colors || [],
        imageUrl: formData.mainImage, // Keep for backward compatibility
        isOffer: formData.isOffer,
        offerPrice: formData.isOffer ? Number(formData.offerPrice) : null,
        isAman: formData.isAman,
        amanPrice: formData.isAman ? Number(formData.amanPrice) : null,
        wholesaleDiscount: Number(formData.wholesaleDiscount) || 0,
        status: 'approved',
        translations: formData.translations // Add translations
      };

      if (editingProduct) {
        result = await updateProduct(editingProduct.id, productDataToSave);
        if (result.success) {
          alert('Product updated successfully');
          setEditingProduct(null);
        }
      } else {
        result = await addProduct(productDataToSave);
        if (result.success) {
          alert('Product added successfully');
        }
      }

      if (result.success) {
        setProductData({
          name: '',
          price: '',
          description: '',
          category: '',
          mainImage: '',
          additionalImages: [],
          sizes: [],
          colors: [],
          isOffer: false,
          offerPrice: '',
          isAman: false,
          amanPrice: '',
          wholesaleDiscount: ''
        });
        loadProducts();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error saving product');
      console.error('Error saving product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let result;
      if (editingCategory) {
        result = await updateCategory(editingCategory.id, categoryData);
        if (result.success) {
          alert('Category updated successfully');
          setEditingCategory(null);
        }
      } else {
        result = await addCategory(categoryData);
        if (result.success) {
          alert('Category added successfully');
        }
      }

      if (result.success) {
        setCategoryData({
          name: '',
          description: ''
        });
        loadCategories();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error saving category');
    } finally {
      setLoading(false);
    }
  };

  const handleBlogSubmit = async (blogData) => {
    setLoading(true);
    setError(null);

    try {
      if (editingBlog) {
        const blogRef = doc(db, 'blogs', editingBlog.id);
        await updateDoc(blogRef, {
          title: blogData.title,
          summary: blogData.summary,
          content: blogData.content,
          imageUrl: blogData.imageUrl,
          author: blogData.author,
          translations: blogData.translations,
          updatedAt: serverTimestamp()
        });
        alert('Post updated successfully');
        setEditingBlog(null);
      } else {
        await addDoc(collection(db, 'blogs'), {
          title: blogData.title,
          summary: blogData.summary,
          content: blogData.content,
          imageUrl: blogData.imageUrl,
          author: blogData.author,
          translations: blogData.translations,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Post added successfully');
      }

      setNewBlog({
        title: '',
        summary: '',
        content: '',
        imageUrl: '',
        author: ''
      });
      fetchBlogs();
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Error saving post');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMarketer = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newMarketer.email,
        newMarketer.password
      );

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        marketerId: userCredential.user.uid,
        name: newMarketer.name,
        email: newMarketer.email,
        role: 'marketer',
        createdAt: new Date().toISOString()
      });

      setNewMarketer({ name: '', email: '', password: '' });
      await fetchMarketers();
      alert(`Marketer ${newMarketer.name} added successfully!`);
    } catch (err) {
      console.error('Error adding marketer:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else {
        setError(`Error adding marketer: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMarketer = (marketerId, marketerEmail) => {
    setShowDeleteForm(marketerId);
    setMarketerCredentials({ email: marketerEmail, password: '' });
  };

  const confirmDeleteMarketer = async (marketerId) => {
    try {
      await signInWithEmailAndPassword(auth, marketerCredentials.email, marketerCredentials.password);

      await auth.currentUser.delete();

      await deleteDoc(doc(db, 'users', marketerId));

      await auth.signOut();

      setMarketerCredentials({ email: '', password: '' });
      setShowDeleteForm(null);
      alert('Marketer deleted successfully! Please sign in again as admin.');
    } catch (err) {
      console.error('Error deleting marketer:', err);
      if (err.code === 'auth/wrong-password') {
        setError('Incorrect password for the marketer. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('Marketer not found in Authentication.');
      } else {
        setError('Failed to delete marketer: ' + err.message);
      }
      setShowDeleteForm(null);

      try {
        await auth.signOut();
      } catch (signOutErr) {
        console.error('Error signing out:', signOutErr);
        setError('Failed to sign out: ' + signOutErr.message);
      }
    }
  };

  const handleSortChange = (e) => {
    setIsSorting(true);
    setSortBy(e.target.value);
    setTimeout(() => setIsSorting(false), 500);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductData({
      name: product.name,
      price: product.price,
      description: product.description,
      category: product.category,
      mainImage: product.mainImage || product.imageUrl || '',
      additionalImages: product.additionalImages || [],
      sizes: product.sizes || [],
      colors: product.colors || [],
      isOffer: product.isOffer || false,
      offerPrice: product.offerPrice || '',
      isAman: product.isAman || false,
      amanPrice: product.amanPrice || '',
      wholesaleDiscount: product.wholesaleDiscount || ''
    });
    window.scrollTo(0, 0);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryData({
      name: category.name,
      description: category.description
    });
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = (type) => {
    if (type === 'product') {
      setEditingProduct(null);
      setProductData({
        name: '',
        price: '',
        description: '',
        category: '',
        mainImage: '',
        additionalImages: [],
        sizes: [],
        colors: [],
        isOffer: false,
        offerPrice: '',
        isAman: false,
        amanPrice: '',
        wholesaleDiscount: ''
      });
    } else {
      setEditingCategory(null);
      setCategoryData({
        name: '',
        description: ''
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const result = await deleteProduct(productId);
        if (result.success) {
          loadProducts();
          alert('Product deleted successfully');
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Error deleting product');
      }
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const result = await deleteCategory(categoryId);
        if (result.success) {
          loadCategories();
          alert('Category deleted successfully');
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Error deleting category');
      }
    }
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    setNewBlog({
      title: blog.title,
      summary: blog.summary,
      content: blog.content,
      imageUrl: blog.imageUrl,
      author: blog.author
    });
    window.scrollTo(0, 0);
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'blogs', blogId));
        fetchBlogs();
        alert('Post deleted successfully');
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('Error deleting post');
      }
    }
  };

  const handleCancelBlogEdit = () => {
    setEditingBlog(null);
    setNewBlog({
      title: '',
      summary: '',
      content: '',
      imageUrl: '',
      author: ''
    });
  };

  const handleWinnerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Combine date and time into announcementDateTime
      const announcementDateTime = new Date(`${winnerData.date}T${winnerData.time}`).toISOString();
      
      const winnerDataToSave = {
        name: winnerData.name,
        prize: winnerData.prize,
        imageUrl: winnerData.imageUrl,
        date: winnerData.date,
        announcementDateTime,
        createdAt: new Date().toISOString()
      };

      if (editingWinner) {
        await updateDoc(doc(db, 'winners', editingWinner.id), winnerDataToSave);
      } else {
        await addDoc(collection(db, 'winners'), winnerDataToSave);
      }

      setWinnerData({
        name: '',
        prize: '',
        imageUrl: '',
        date: '',
        announcementDateTime: '',
        time: ''
      });
      setEditingWinner(null);
      
      // Refresh winners list
      const winnersSnapshot = await getDocs(collection(db, 'winners'));
      const winnersData = winnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWinners(winnersData);
      
    } catch (err) {
      console.error('Error saving winner:', err);
      setError('Failed to save winner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWinner = async (winnerId) => {
    if (window.confirm('Are you sure you want to delete this winner?')) {
      try {
        await deleteDoc(doc(db, 'winners', winnerId));
        setWinners(winners.filter(winner => winner.id !== winnerId));
      } catch (err) {
        console.error('Error deleting winner:', err);
        setError('Failed to delete winner');
      }
    }
  };

  const handleEditWinner = (winner) => {
    const announcementDate = new Date(winner.announcementDateTime);
    
    setEditingWinner(winner);
    setWinnerData({
      name: winner.name,
      prize: winner.prize,
      imageUrl: winner.imageUrl,
      date: winner.date,
      announcementDateTime: winner.announcementDateTime,
      time: announcementDate.toTimeString().slice(0, 5) // Extract HH:MM from the time
    });
    window.scrollTo(0, 0);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button 
            className={`tab-button ${activeTab === 'offers' ? 'active' : ''}`}
            onClick={() => setActiveTab('offers')}
          >
            Offers
          </button>
          <button 
            className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button 
            className={`tab-button ${activeTab === 'blogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('blogs')}
          >
            Blog
          </button>
          <button 
            className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders
          </button>
          <button 
            className={`tab-button ${activeTab === 'marketers' ? 'active' : ''}`}
            onClick={() => setActiveTab('marketers')}
          >
            Marketers
          </button>
          <button 
            className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            Messages
          </button>
          <button 
            className={`tab-button ${activeTab === 'winners' ? 'active' : ''}`}
            onClick={() => setActiveTab('winners')}
          >
            Winners
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'products' && (
        <div className="products-section">
          <EnhancedProductFormWithTranslation
            onSubmit={handleEnhancedProductSubmit}
            onCancel={() => handleCancelEdit('product')}
            initialData={editingProduct ? productData : null}
            categories={categories}
            loading={loading}
          />
          <div className="items-list">
            <div className="list-header">
              <h3>Products List</h3>
              <div className="sort-container">
                <label>Sort by:</label>
                <select
                  className={`sort-select ${isSorting ? 'sorting' : ''}`}
                  value={sortBy}
                  onChange={handleSortChange}
                  disabled={isSorting}
                >
                  <option value="newest">Newest</option>
                  <option value="priceAsc">Price: Low to High</option>
                  <option value="priceDesc">Price: High to Low</option>
                </select>
              </div>
            </div>
            {products.length === 0 ? (
              <div className="no-products">
                <p>No products available</p>
              </div>
            ) : (
              <div className="products-grid">
                {products.map(product => (
                  <div key={product.id} className="item-card product-card">
                    <div className="product-image">
                      <img src={product.mainImage || product.imageUrl} alt={product.name} />
                    </div>
                    <div className="product-details">
                      <h4>{product.name}</h4>
                      <p className="description">{product.description}</p>
                      <p className="price">${product.price}</p>
                      <div className="card-actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditProduct(product)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'offers' && (
        <div className="offers-section">
          <h2>Special Offers</h2>
          <div className="products-grid">
            {products.filter(product => product.isOffer).map(product => (
              <div key={product.id} className="item-card product-card offer">
                <div className="product-image">
                  <img src={product.imageUrl} alt={product.name} />
                </div>
                <div className="product-details">
                  <h4>{product.name}</h4>
                  <p className="description">{product.description}</p>
                  <div className="price-info">
                    <p className="original-price">Original Price: ${product.price}</p>
                    <p className="offer-price">Offer Price: ${product.offerPrice}</p>
                  </div>
                  <div className="card-actions">
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h2 className="aman-section-title">Aman Products</h2>
          <div className="products-grid">
            {products.filter(product => product.isAman).map(product => (
              <div key={product.id} className="item-card product-card aman">
                <div className="aman-badge">Aman</div>
                <div className="product-image">
                  <img src={product.imageUrl} alt={product.name} />
                </div>
                <div className="product-details">
                  <h4>{product.name}</h4>
                  <p className="description">{product.description}</p>
                  <p className="price">Price: ${product.price}</p>
                  <div className="card-actions">
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="categories-section">
          <form onSubmit={handleCategorySubmit} className="admin-form">
            <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
            <input
              type="text"
              placeholder="Category Name"
              value={categoryData.name}
              onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
              required
            />
            <textarea
              placeholder="Category Description"
              value={categoryData.description}
              onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
              required
            />
            <div className="form-buttons">
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingCategory ? 'Update Category' : 'Add Category'}
              </button>
              {editingCategory && (
                <button type="button" className="cancel-btn" onClick={() => handleCancelEdit('category')}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>

          <div className="items-list">
            <h3>Categories List</h3>
            {categories.length === 0 ? (
              <div className="no-categories">
                <p>No categories available</p>
              </div>
            ) : (
              <div className="categories-grid">
                {categories.map(category => (
                  <div key={category.id} className="item-card">
                    <h4>{category.name}</h4>
                    <p className="description">{category.description}</p>
                    <div className="card-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditCategory(category)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'blogs' && (
        <div className="blogs-section">
          <EnhancedBlogFormWithTranslation
            onSubmit={handleBlogSubmit}
            onCancel={handleCancelBlogEdit}
            initialData={editingBlog ? newBlog : null}
            loading={loading}
          />

          <div className="items-list">
            <h3>Blog Posts</h3>
            {blogs.length === 0 ? (
              <div className="no-blogs">
                <p>No blog posts available</p>
              </div>
            ) : (
              <div className="blogs-grid">
                {blogs.map(blog => (
                  <div key={blog.id} className="item-card blog-card">
                    <div className="blog-image">
                      <img src={blog.imageUrl} alt={blog.title} />
                    </div>
                    <div className="blog-details">
                      <h4>{blog.title}</h4>
                      <p className="summary">{blog.summary}</p>
                      <p className="author">Author: {blog.author}</p>
                      <div className="card-actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditBlog(blog)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteBlog(blog.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="orders-tab-wrapper">
          <Orders />
        </div>
      )}

      {activeTab === 'marketers' && (
        <div className="marketers-section">
          <h2>Add New Marketer</h2>
          <form onSubmit={handleAddMarketer} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={newMarketer.name}
                onChange={(e) => setNewMarketer({ ...newMarketer, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={newMarketer.email}
                onChange={(e) => setNewMarketer({ ...newMarketer, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={newMarketer.password}
                onChange={(e) => setNewMarketer({ ...newMarketer, password: e.target.value })}
                required
              />
            </div>
            <button type="submit">Add Marketer</button>
          </form>

          <h2>Marketers List</h2>
          {marketers.length === 0 ? (
            <div className="no-marketers">No marketers found</div>
          ) : (
            <div className="marketers-grid">
              {marketers.map((marketer) => (
                <div key={marketer.id} className="marketer-card">
                  <div className="marketer-info">
                    <h3>{marketer.name}</h3>
                    <p>{marketer.email}</p>
                    <p>Created: {new Date(marketer.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="marketer-actions">
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteMarketer(marketer.id, marketer.email)}
                    >
                      Delete
                    </button>
                  </div>

                  {showDeleteForm === marketer.id && (
                    <div className="delete-form">
                      <h3>Delete Marketer</h3>
                      <p>Please enter the marketer's password to confirm deletion:</p>
                      <input
                        type="email"
                        value={marketerCredentials.email}
                        disabled
                      />
                      <input
                        type="password"
                        placeholder="Marketer Password"
                        value={marketerCredentials.password}
                        onChange={(e) =>
                          setMarketerCredentials({ ...marketerCredentials, password: e.target.value })
                        }
                        required
                      />
                      <div className="form-buttons">
                        <button
                          type="button"
                          onClick={() => confirmDeleteMarketer(marketer.id)}
                          disabled={!marketerCredentials.password}
                        >
                          Confirm Delete
                        </button>
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={() => setShowDeleteForm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="messages-section">
          <Messages />
        </div>
      )}

      {activeTab === 'winners' && (
        <div className="winners-section">
          <form onSubmit={handleWinnerSubmit} className="admin-form">
            <h2>{editingWinner ? 'Edit Winner' : 'Add New Winner'}</h2>
            <input
              type="text"
              placeholder="Winner Name"
              value={winnerData.name}
              onChange={(e) => setWinnerData({ ...winnerData, name: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Prize Won"
              value={winnerData.prize}
              onChange={(e) => setWinnerData({ ...winnerData, prize: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Announcement Date"
              value={winnerData.date}
              onChange={(e) => setWinnerData({ ...winnerData, date: e.target.value })}
              required
            />
            <input
              type="time"
              placeholder="Announcement Time"
              value={winnerData.time}
              onChange={(e) => setWinnerData({ ...winnerData, time: e.target.value })}
              required
            />
            <ImageUpload
              label="Winner Image"
              initialUrl={winnerData.imageUrl}
              onUpload={url => setWinnerData({ ...winnerData, imageUrl: url })}
            />
            <div className="form-buttons">
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingWinner ? 'Update Winner' : 'Add Winner'}
              </button>
              {editingWinner && (
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={() => {
                    setEditingWinner(null);
                    setWinnerData({ name: '', prize: '', imageUrl: '', date: '', announcementDateTime: '', time: '' });
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>

          <div className="items-list">
            <h3>Winners List</h3>
            {winners.length === 0 ? (
              <div className="no-items">
                <p>No winners added yet</p>
              </div>
            ) : (
              <div className="winners-grid">
                {winners.map(winner => (
                  <div key={winner.id} className="item-card winner-card">
                    <div className="winner-image">
                      <img src={winner.imageUrl} alt={winner.name} />
                    </div>
                    <div className="winner-details">
                      <h4>{winner.name}</h4>
                      <p className="prize">Prize: {winner.prize}</p>
                      <p className="date">Date: {new Date(winner.date).toLocaleDateString()}</p>
                      <div className="card-actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditWinner(winner)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteWinner(winner.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;